// src/pages/PreLoginSplashPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../scripts/api";
import { LogIn, PlusCircle } from "lucide-react";
import Modal from "../components/Modal";
import logoLight from "../assets/light_logo.jpg";
import darkGrBg from "../assets/dark_gr_bg.jpg";
import darkBg from "../assets/dark_bg.jpg";

function safeParseUser() {
  try {
    return JSON.parse(sessionStorage.getItem("auth_user") || "null");
  } catch {
    return null;
  }
}

export default function PreLoginSplashPage() {
  const nav = useNavigate();

  const [dbs, setDbs] = useState([]);
  const [users, setUsers] = useState([]);

  const [dbId, setDbId] = useState(sessionStorage.getItem("active_db") || "");
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");

  const [newDbName, setNewDbName] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // telegram test status
  const [tgMsg, setTgMsg] = useState("");

  // auth state (fixes eslint: setAuthUser / safeParseUser)
  const [authUser, setAuthUser] = useState(() => safeParseUser());
  const [animatingOut, setAnimatingOut] = useState(false);
  const animTimerRef = useRef(null);

  const theme = localStorage.getItem("sna_theme") || "system";
  const bgImg = useMemo(() => {
    if (theme === "dark") return darkGrBg;
    if (theme === "light") return logoLight;
    return darkBg;
  }, [theme]);

  // keep auth state synced (same-tab + other tabs)
  useEffect(() => {
    setAuthUser(safeParseUser());

    const onStorage = (e) => {
      if (e.key === "auth_user" || e.key === "auth_token") {
        setAuthUser(safeParseUser());
      }
    };
    window.addEventListener("storage", onStorage);

    // same-tab updates: poll for changes (cheap + reliable)
    let last = sessionStorage.getItem("auth_user") || "";
    const t = setInterval(() => {
      const cur = sessionStorage.getItem("auth_user") || "";
      if (cur !== last) {
        last = cur;
        setAuthUser(safeParseUser());
      }
    }, 500);

    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(t);
    };
  }, []);

  // if already logged in, skip splash
  useEffect(() => {
    const tok = sessionStorage.getItem("auth_token") || "";
    if (tok) nav("/home", { replace: true });
  }, [nav, authUser]);

  const load = async () => {
    setBusy(true);
    setMsg("");
    try {
      const [a, b] = await Promise.all([api.get("/public/dbs"), api.get("/public/users")]);
      const dbRows = Array.isArray(a.data) ? a.data : [];
      const userRows = Array.isArray(b.data) ? b.data : [];
      setDbs(dbRows);
      setUsers(userRows);

      if (!dbId && dbRows[0]?.dbId) setDbId(dbRows[0].dbId);
      if (!userId && userRows[0]?.id) setUserId(String(userRows[0].id));
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load login data");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testTelegram = async () => {
    setBusy(true);
    setTgMsg("");
    try {
      const who = users.find((x) => String(x.id) === String(userId))?.email || "unknown";
      const { data } = await api.post("/public/telegram/test", {
        topicId: 63,
        text: `🧪 IMS Telegram test from splash\nuser: ${who}\ntime: ${new Date().toISOString()}`,
      });

      setTgMsg(data?.ok ? "✅ Telegram test sent (topic 4)." : "⚠️ Telegram returned non-ok.");
    } catch (e) {
      const d = e?.response?.data;
      setTgMsg(`❌ Telegram test failed: ${d?.reason || d?.error || d?.message || e?.message || "unknown error"}`);
    } finally {
      setBusy(false);
    }
  };

  const login = async () => {
    if (!dbId) return setMsg("Select a database");
    if (!userId) return setMsg("Select a user");
    if (!password) return setMsg("Enter password");

    setBusy(true);
    setMsg("");
    try {
      sessionStorage.setItem("active_db", dbId);

      const u = users.find((x) => String(x.id) === String(userId));
      if (!u?.email) throw new Error("Selected user missing email");

      const { data } = await api.post("/auth/login", { email: u.email, password });

      const token = data?.token || "";
      if (!token) throw new Error("Login returned no token");

      sessionStorage.setItem("auth_token", token);

      // IMPORTANT: persist user so NavBar can show display name immediately
      if (data?.user) {
        sessionStorage.setItem("auth_user", JSON.stringify(data.user));
        setAuthUser(data.user);
      } else {
        // fallback: keep auth_user consistent (avoid stale name)
        sessionStorage.removeItem("auth_user");
        setAuthUser(null);
      }

      // pre-login splash slides up before routing
      setAnimatingOut(true);
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
      animTimerRef.current = setTimeout(() => {
        nav("/home", { replace: true });
      }, 420);
    } catch (e) {
      setMsg(e?.response?.data?.error || e?.message || "Login failed");
      setAnimatingOut(false);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    return () => {
      if (animTimerRef.current) clearTimeout(animTimerRef.current);
    };
  }, []);

  const createDb = async () => {
    const name = String(newDbName || "").trim();
    if (!name) return;

    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/db/create", {
        name,
        template: "inventory",
        extras: { finance: true, purchase_orders: true, tracking: true, date_tracking: true },
      });

      const createdDbId = data?.dbId;
      if (!createdDbId) throw new Error("DB created but no dbId returned");

      await load();
      setDbId(createdDbId);
      setNewDbName("");
      setMsg("Database created. Select it and login.");
      setModalOpen(false);
    } catch (e) {
      setMsg(e?.response?.data?.error || e?.message || "Failed to create database");
    } finally {
      setBusy(false);
    }
  };

  const narrowCardStyle = {
    width: "100%",
    maxWidth: 520,
    marginLeft: "auto",
    marginRight: "auto",
    background: "rgba(255,255,255,0.02)",
  };

  return (
    <div
      className="container"
      style={{
        textAlign: "center",
        padding: "50px 20px",
        maxWidth: "800px",
        margin: "auto",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* blurred bg */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${bgImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.2,
          filter: "blur(3px)",
          transform: animatingOut ? "translateY(-110%)" : "translateY(0)",
          transition: "transform 420ms ease",
        }}
      />

      {/* content wrapper slides up on login */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          transform: animatingOut ? "translateY(-110%)" : "translateY(0)",
          transition: "transform 420ms ease",
        }}
      >
        <h1
          style={{
            fontWeight: 900,
            color: "var(--primary-color)",
            textShadow: "0 0 5px rgba(0, 255, 0, 0.3)",
            animation: "pulse 2s infinite",
          }}
        >
          Inventory Management
        </h1>

        <p
          className="muted"
          style={{
            fontSize: "18px",
            marginBottom: "28px",
            textShadow: "0 0 3px rgba(0, 255, 0, 0.2)",
          }}
        >
          React • SQLite • Python • JS.
        </p>

        {msg ? (
          <div className="muted" style={{ marginTop: 10 }}>
            {msg}
          </div>
        ) : null}
        {tgMsg ? (
          <div className="muted" style={{ marginTop: 10 }}>
            {tgMsg}
          </div>
        ) : null}

        <div style={{ display: "grid", gap: 20, justifyItems: "center" }}>
          <div className="card" style={{ ...narrowCardStyle, marginTop: 12 }}>
            <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ fontWeight: 900 }}>Select Database</div>
              <button className="btn-outline" onClick={load} disabled={busy}>
                Refresh
              </button>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginTop: 12 }}>
              {dbs.map((d) => (
                <button
                  key={d.dbId}
                  onClick={() => setDbId(d.dbId)}
                  className="card"
                  style={{
                    background: dbId === d.dbId ? "rgba(58, 248, 58, 0.658)" : "transparent",
                    border: "1px solid var(--card-border)",
                    borderRadius: 12,
                    padding: "10px 20px",
                    cursor: "pointer",
                    position: "relative",
                    animation: "fadeIn 1.0s ease-in-out",
                  }}
                >
                  {d.name} ({d.dbId})
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "green",
                      marginLeft: 10,
                    }}
                  />
                </button>
              ))}
            </div>

            <button
              className="btn"
              onClick={() => setModalOpen(true)}
              disabled={busy}
              style={{ marginTop: 20, boxShadow: "0 0 10px rgba(0, 255, 0, 0.5)" }}
            >
              <PlusCircle size={18} style={{ marginRight: 8 }} />
              Create New DB
            </button>

            <div className="muted" style={{ marginTop: 8 }}>
              This <code>assets</code> list comes from <code>backend/registry.sqlite</code> → <code>databases</code>{" "}
              table.
            </div>
          </div>

          <div className="card" style={{ ...narrowCardStyle, marginTop: 0 }}>
            <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 900 }}>Login - Password: test</div>
              <LogIn size={18} />
            </div>

            <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>User</div>
                <select
                  className="input"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  style={{ borderRadius: 12, boxShadow: "0 0 5px rgba(0, 255, 0, 0.2)" }}
                >
                  <option value="">Select user…</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.display_name ? `${u.display_name} — ` : ""}
                      {u.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Password</div>
                <input
                  className="input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ borderRadius: 12, boxShadow: "0 0 5px rgba(0, 255, 0, 0.2)" }}
                />
              </div>

              <button
                className="btn"
                onClick={login}
                disabled={busy || animatingOut}
                style={{ boxShadow: "0 0 10px rgba(0, 255, 0, 0.5)", borderRadius: 12 }}
              >
                {busy ? "Signing in…" : "Sign in"}
              </button>

              {/* optional debug button you already had 
              <button className="btn-outline" onClick={testTelegram} disabled={busy} style={{ borderRadius: 12 }}>
                Test Telegram
              </button>*/}
            </div>
          </div>
        </div>

        {modalOpen && (
          <Modal onClose={() => setModalOpen(false)}>
            <h2>Create New Database</h2>
            <input
              className="input"
              placeholder="Database name (e.g. SNA 2026)"
              value={newDbName}
              onChange={(e) => setNewDbName(e.target.value)}
              style={{ width: "100%", marginBottom: 10 }}
            />
            <button className="btn" onClick={createDb} disabled={busy || !newDbName.trim()}>
              Create
            </button>
          </Modal>
        )}

        <div style={{ marginTop: 40 }}>
          <a
            href="https://almagestlabs.com/"
            target="_blank"
            rel="noreferrer"
            className="muted"
            style={{
              display: "inline-flex",
              gap: 8,
              alignItems: "center",
              fontWeight: 900,
              textDecoration: "none",
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid var(--card-border)",
              background: "rgba(255,255,255,0.02)",
            }}
          >
            <span style={{ color: "orange" }}>Almagest</span>
            <span style={{ color: "white" }}>Labs</span>
            <span className="muted" style={{ fontWeight: 700 }}>
              ↗
            </span>
          </a>
        </div>
      </div>
    </div>
  );
}
